import { createContext, useContext, useMemo } from "react";

import { recordHome, recordHomeUrl } from "src/api/records";

/**
 * Where/how a citation click is surfaced.
 *
 * The main Assistant view opens its right-hand sources sidebar on click
 * (CitationLink's default, driven by UIStore). Sidebar chat panels
 * (Equipment AI's ERChatPanel) have no such sidebar, so they provide this
 * context to redirect the click — e.g. to open the source's dataset record
 * in a new browser tab. When no provider is present, CitationLink keeps its
 * default sidebar behavior, so other chat surfaces are unaffected.
 */
export interface CitationPresentationSource {
  /** Gap-aware inline citation number; null for an unresolved/unnumbered cite. */
  citationNumber: number | null;
  /** Row identifier for tabular datasets (SourceDataRecord.source_uid). */
  sourceUid?: string;
  /** SourceDataRecord id (used for the document deep-link). */
  sdrId?: number;
  datasetId?: number;
  /** Dataset type id; tabular types (1/4/5) deep-link to the table viewer. */
  datasetTypeId?: number;
  /** 1-indexed page numbers for document chunks. */
  pageNumber?: number[];
  /** Cited chunk id, for record-view deep links that scroll to the passage. */
  chunkId?: number | string;
  /**
   * Project-folder file citations: SDRs scoped by project_folder_fk carry
   * no dataset id. The AA Projects chat provides a presentation that
   * resolves these into its file rail (spec §5.10).
   */
  folderId?: number;
  fileName?: string;
  /**
   * True when the cited source is present in the current response's
   * responseSources (CitationLink's hasSource). Presentations with an
   * in-panel sources view use this to decide between scrolling to the
   * card and deep-linking out.
   */
  hasSource?: boolean;
}

export interface CitationPresentation {
  /**
   * Handle a citation click. Return `false` to DECLINE the click —
   * CitationLink then falls through to its default handling (the annotated
   * in-chat viewer + sources sidebar), which the host surface must support
   * (e.g. by mounting InChatDocumentViewer). Any other return (including
   * undefined) means handled.
   */
  openCitation: (source: CitationPresentationSource) => void | boolean;
  /**
   * Set when this presentation renders its own sources panel and can
   * surface citations whose source is in responseSources even without a
   * dataset/folder id to deep-link to. CitationLink then routes hasSource
   * clicks here instead of falling back to the main Assistant sidebar.
   */
  handlesInResponseSources?: boolean;
}

const CitationPresentationContext = createContext<CitationPresentation | null>(
  null,
);

export const CitationPresentationProvider =
  CitationPresentationContext.Provider;

export const useCitationPresentation = (): CitationPresentation | null =>
  useContext(CitationPresentationContext);

// Mirrors backend TABULAR_DATASET_TYPES (mre/__init__.py): TABULAR, ACTIVITY,
// ATOMRESULTS. These store one SourceDataRecord per row, so source_uid is the
// row identifier and the table viewer can filter to it.
export const TABULAR_DATASET_TYPE_IDS = [1, 4, 5];

/** True for a dataset whose type stores one row per SourceDataRecord. */
export const isTabularDatasetType = (
  datasetTypeId: number | null | undefined,
): boolean =>
  datasetTypeId != null && TABULAR_DATASET_TYPE_IDS.includes(datasetTypeId);

/**
 * URL to a single tabular row's record page. Tabular rows have no PDF/page to
 * render in the document viewer, so citations to them open the record's
 * predictions view (`/datasets/:dataset_id/predictions/:source_uid`) instead.
 * That route takes the dataset id as-is — remote/replicated datasets keep
 * their negative ids (e.g. /datasets/-1/predictions/53813).
 */
export const tabularRowViewerUrl = (
  datasetId: number,
  sourceUid: string,
): string =>
  `/datasets/${datasetId}/predictions/${encodeURIComponent(sourceUid)}`;

/**
 * Citation presentation that opens the source's record in a new tab: tabular
 * rows (always dataset-homed) open the table viewer filtered to the row's
 * identifier; documents open the record view at the cited page, wherever the
 * SDR actually lives (dataset or project folder) — a folder-homed document
 * has no `datasetId` at all, only `folderId`.
 */
export const useDatasetViewerCitationPresentation =
  (): CitationPresentation => {
    return useMemo(
      () => ({
        openCitation: ({
          sourceUid,
          sdrId,
          datasetId,
          datasetTypeId,
          pageNumber,
          chunkId,
          folderId,
        }: CitationPresentationSource) => {
          let url: string;
          if (
            folderId == null &&
            datasetId != null &&
            isTabularDatasetType(datasetTypeId) &&
            sourceUid
          ) {
            url = tabularRowViewerUrl(datasetId, sourceUid);
          } else if (sdrId != null) {
            // folderId is authoritative when present: a citation can carry a
            // dataset id alongside a real folder id (e.g. a display/grouping
            // dataset unrelated to the SDR's actual scope), and recordHome()
            // otherwise prioritizes any dataset FK it's handed.
            const home = recordHome({
              dataset_fk: folderId == null ? datasetId : undefined,
              project_folder_fk: folderId,
            });
            if (!home) return;
            const page = Array.isArray(pageNumber) ? pageNumber[0] : undefined;
            url = recordHomeUrl(home, sdrId, { chunkId, page });
          } else if (folderId == null && datasetId != null) {
            // Remote/replicated datasets carry negative ids; the viewer
            // routes expect the positive id (matches Math.abs normalization
            // elsewhere, e.g. DatasetViewer / SearchResultsDisplay).
            url = `/datasets/${Math.abs(datasetId)}/viewer`;
          } else {
            return;
          }

          window.open(url, "_blank", "noopener,noreferrer");
        },
      }),
      [],
    );
  };
